import { P2GBox, P2GGrid, P2GRating, P2GText } from '@parcel2go/core-ui';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

export type Props = {
  rating: number;
  ratingTypes: { name: string; value: number }[];
};

const toRatingLabel = (type: string) => {
  switch (type) {
    case 'TrackingDetail':
      return 'Tracking Detail';
    case 'DeliveredOnTime':
      return 'Delivered on Time %';
    default:
      return type;
  }
};

const courierRatingMessages = [
  {
    rating: 5,
    message: `You can rely on couriers with our 5 star rating to provide exceptional service, time after time. 5 star couriers consistently deliver parcels safely and on-time and their products are among the best courier services you can buy.`
  },
  {
    rating: 4,
    message: `A 4 star rating means you can trust the courier to provide a high-quality parcel delivery service. Expect your parcel to arrive efficiently and, on the rare occasion that an issue arises, it will be resolved quickly.`
  },
  {
    rating: 3,
    message: `You can expect a decent standard of parcel delivery from a 3 star service, however the courier may have provided inconsistent or delayed services in the past. Should you experience any issues with your delivery, our Customer Support team are on-hand to help 24 hours a day.`
  },
  {
    rating: 2,
    message: `If a courier has a rating of 2 stars, they may have a track record of inconsistency in delivering parcels on time or may not provide useful information to customers, such as tracking updates. For best results, we recommend using services with 4 or 5 star ratings.`
  },
  {
    rating: 1,
    message: `A 1 star courier rating suggests that you may receive a lower-standard delivery experience than you are used to. To avoid disappointment, Parcel2Go recommends that you select a 4 or 5 star service.`
  }
];

const QuoteCardRating = ({ ratingTypes, rating }: Props) => {
  const { t } = useTranslation(TranslationNamespaces.QUOTE);

  if (!ratingTypes || rating === undefined) {
    return null;
  }

  const roundedRating = Math.floor(rating);
  const ratingMessage = courierRatingMessages.find((m) => m.rating === roundedRating)?.message;

  return (
    <P2GBox p={1} pb={2} display="flex" flexDirection="column" gap={2}>
      <P2GText variant="body2" component="p">
        <Trans i18nKey={`QUOTE_RATING_MESSAGE_${roundedRating}`} t={t} defaults={ratingMessage} />
      </P2GText>
      <P2GText variant="body2" component="p">
        <Trans
          i18nKey={`QUOTE_RATING_MESSAGE_NOTE`}
          t={t}
          defaults={'*Ratings are based upon actual courier performance'}
        />
      </P2GText>
      {ratingTypes.length > 0 && (
        <P2GGrid container direction="column" spacing={1}>
          {ratingTypes.map((x) => (
            <P2GGrid key={x.name} item>
              <P2GBox display="flex" alignItems="center" gap={1}>
                <P2GRating rating={x.value} size="small" />
                <P2GText variant="body2">
                  <Trans i18nKey={`QUOTE_RATING`} t={t} defaults={toRatingLabel(x.name)} />
                </P2GText>
              </P2GBox>
            </P2GGrid>
          ))}
        </P2GGrid>
      )}
    </P2GBox>
  );
};

export default QuoteCardRating;
